import { DefaultProjectorFn, MemoizedSelector, createSelector } from '@ngrx/store';

import { AppState } from '../app.state';
import { HemsFlowType } from '../energy-historical-data/energy-historical-data.helpers';
import { EnergyHistoricalDataState } from '../energy-historical-data';
import { AnalyticsState } from './analytics.state';
import { AvailableFeature } from './analytics.model';

export namespace AnalyticsSelectors {
  export const analyticsState = (state: AppState): AnalyticsState => state.analytics;
  export const energyHistoricalData = (state: AppState): EnergyHistoricalDataState =>
    state.energyHistoricalData;

  export const selectPropertySinceDataLoaded = createSelector(
    analyticsState,
    (state: AnalyticsState): boolean => {
      const devicesWithAvailableFeaturesLoaded =
        !!state.devicesWithAvailableFeatures.error || !!state.devicesWithAvailableFeatures?.value;
      const hemsFeaturesPresentSince =
        !!state.hemsFeaturesPresentSince.error || !!state.hemsFeaturesPresentSince?.value;

      return hemsFeaturesPresentSince && devicesWithAvailableFeaturesLoaded;
    }
  );

  export const selectAvailableChronosFeature = (
    featureName: string
  ): MemoizedSelector<AppState, AvailableFeature, DefaultProjectorFn<AvailableFeature>> =>
    createSelector(
      analyticsState,
      (state: AnalyticsState): AvailableFeature => {
        return state.devicesWithAvailableFeatures.value.properties.find((entry) => {
          return entry.property === featureName;
        });
      }
    );

  export const selectEarlierstAvailableChronosFeature = createSelector(
    analyticsState,
    (state: AnalyticsState): string => {
      let earliestStartDatetime: string =
        state.devicesWithAvailableFeatures.value?.properties[0]?.start_datetime;

      state.devicesWithAvailableFeatures?.value?.properties.forEach((feature) => {
        if (feature.start_datetime < earliestStartDatetime) {
          earliestStartDatetime = feature.start_datetime;
        }
      });

      return earliestStartDatetime;
    }
  );

  export const selectAvailableHemsFeature = (
    featureName: string
  ): MemoizedSelector<AppState, AvailableFeature, DefaultProjectorFn<AvailableFeature>> =>
    createSelector(
      analyticsState,
      (state: AnalyticsState): AvailableFeature => {
        return state.hemsFeaturesPresentSince.value.properties.find((entry) => {
          return entry.property === featureName;
        });
      }
    );

  export const selectEarlierstAvailableHemsFeature = createSelector(
    analyticsState,
    (state: AnalyticsState): string => {
      let earliestStartDatetime: string =
        state.hemsFeaturesPresentSince.value?.properties[0]?.start_datetime;

      state.hemsFeaturesPresentSince?.value?.properties.forEach((feature) => {
        if (feature.start_datetime < earliestStartDatetime) {
          earliestStartDatetime = feature.start_datetime;
        }
      });

      return earliestStartDatetime;
    }
  );

  export const selectEarlierstDateFeatureForHemsAnalysisTab = createSelector(
    energyHistoricalData,
    selectEarlierstAvailableChronosFeature,
    selectEarlierstAvailableHemsFeature,
    (
      energyHistoricalDataState: EnergyHistoricalDataState,
      selectEarlierstAvailableChronosFeatureSelector: string,
      selectEarlierstAvailableHemsFeatureSelector: string
    ): string => {
      if (energyHistoricalDataState.hemsAnalysisSelectedTab === HemsFlowType.Thermal) {
        return selectEarlierstAvailableChronosFeatureSelector;
      } else {
        return selectEarlierstAvailableHemsFeatureSelector;
      }
    }
  );
}
